import type { ConsolePorts } from './console-ports'
import type {
	ConsoleEffect,
	ConsoleEvent,
	ConsoleTimer,
} from './model/presentation-machine'
import type { ConsoleMessage } from './model/presenter-messages'
import { projectorPath, projectorWindowName } from './model/presenter-url'
import {
	chooseProjector,
	DEFAULT_POPUP_FEATURES,
	popupFeaturesOn,
} from './model/screens'

/** A presentation window that never reports is given up after this. */
export const OPENING_TIMEOUT_MS = 15000
/** A presentation window that went away gets this long to come back (a reload). */
export const RELOAD_GRACE_MS = 10000

const TIMER_MS: Record<ConsoleTimer, number> = {
	opening: OPENING_TIMEOUT_MS,
	grace: RELOAD_GRACE_MS,
}

type Options = {
	liveSessionId: string
	ports: ConsolePorts
	/** The screens as last read, to place the window on the projector. */
	screens: { current: ScreenDetails | undefined }
	/** To the presentation window over the link, while one runs. */
	toProjector: (message: ConsoleMessage) => void
	/** Into the store the presentation window renders from. */
	showBlank: (blank: boolean) => void
	warn: (message: string, error: unknown) => void
}

export type ConsoleEffects = {
	run: (effect: ConsoleEffect, send: (event: ConsoleEvent) => void) => void
	/** The presentation window made itself known (to a reloaded console). */
	found: (window: Window) => void
	dispose: () => void
}

/**
 * Runs the state machine's effects. Synchronously: `open-window` and
 * `delegate-fullscreen` need the very click that caused them. It holds what
 * is not data - the presentation window's `WindowProxy` and the timers - and
 * answers with events where the machine waits for an outcome.
 */
export const createConsoleEffects = ({
	liveSessionId,
	ports,
	screens,
	toProjector,
	showBlank,
	warn,
}: Options): ConsoleEffects => {
	let popup: Window | null = null
	const timers = new Map<ConsoleTimer, number>()

	const cancel = (timer: ConsoleTimer): void => {
		const id = timers.get(timer)
		if (id !== undefined) {
			ports.clock.clearTimeout(id)
			timers.delete(timer)
		}
	}

	const run = (
		effect: ConsoleEffect,
		send: (event: ConsoleEvent) => void
	): void => {
		switch (effect.type) {
			case 'ask-screens':
				ports.readScreens().then(details => {
					screens.current = details ?? screens.current
					send({
						type: 'screens-answered',
						granted: !!details,
						clickActive: ports.clickStillActive(),
					})
				})
				return
			case 'open-window': {
				const details = effect.place ? screens.current : undefined
				const target =
					details && chooseProjector(details.screens, details.currentScreen)
				const id = ports.randomId()
				const opened = ports.openWindow(
					projectorPath(liveSessionId, id),
					projectorWindowName(liveSessionId),
					target ? popupFeaturesOn(target) : DEFAULT_POPUP_FEATURES
				)
				if (!opened) {
					send({ type: 'window-blocked' })
					return
				}
				popup = opened
				send({
					type: 'window-opened',
					id,
					at: ports.clock.now(),
					placed: !!target,
				})
				return
			}
			case 'delegate-fullscreen':
				if (!popup || popup.closed) {
					send({ type: 'delegation-failed' })
					return
				}
				ports.delegateFullscreen(popup, {
					kind: 'go-fullscreen',
					presentation: effect.id,
				})
				return
			case 'enter-fullscreen-here':
				ports
					.enterFullscreen()
					.catch(error => warn('Full screen was refused', error))
				return
			case 'leave-fullscreen-here':
				ports.leaveFullscreen().catch(() => undefined)
				return
			case 'close-window':
				toProjector({ kind: 'end' })
				popup?.close()
				popup = null
				return
			case 'save':
				ports.storage.write(liveSessionId, effect.saved)
				return
			case 'show-blank':
				showBlank(effect.blank)
				return
			case 'start-timer': {
				const { timer } = effect
				cancel(timer)
				timers.set(
					timer,
					ports.clock.setTimeout(() => {
						timers.delete(timer)
						send({ type: 'timer-over', timer })
					}, TIMER_MS[timer])
				)
				return
			}
			case 'cancel-timer':
				cancel(effect.timer)
				return
		}
	}

	return {
		run,
		found: window => {
			popup = window
		},
		dispose: () => {
			timers.forEach(id => ports.clock.clearTimeout(id))
			timers.clear()
		},
	}
}
